import { quoteQuery } from "@common/api/quoting/quote"
import { useUpdateQuotePhaseMutation } from "@common/api/quoting/quote-phase"
import { currentUserQuery } from "@common/api/users"
import { hasPermission } from "@common/lib/permissions"
import { handleError } from "@common/utils"
import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router"
import { PhaseNameDialog } from "@/components/phase/phase-name-dialog"
import useCustomToast from "@/hooks/use-custom-toast"

export const Route = createFileRoute(
  "/_layout/quotes_/$quoteId/phases/$phaseId/rename",
)({
  beforeLoad: async ({ context: { queryClient }, params, search }) => {
    const me = await queryClient.ensureQueryData(currentUserQuery())
    if (!hasPermission(me, "quote:update")) {
      throw redirect({
        to: "/quotes/$quoteId",
        params: { quoteId: params.quoteId },
        search,
      })
    }
  },
  component: RenamePhaseDialog,
})

function RenamePhaseDialog(): React.ReactElement {
  const { quoteId, phaseId } = Route.useParams()
  const { data: quote } = useSuspenseQuery(quoteQuery({ quote_id: quoteId }))
  const navigate = useNavigate()
  const showToast = useCustomToast()
  const mutation = useUpdateQuotePhaseMutation()
  const phase = quote.active_phases.find(({ id }) => id === phaseId)
  const close = () =>
    navigate({ to: "/quotes/$quoteId", params: { quoteId }, search: true })

  return (
    <PhaseNameDialog
      title="Rename phase"
      description="The new name shows wherever this phase groups the quote's tasks."
      name={phase?.name ?? ""}
      action="Rename"
      pending={mutation.isPending}
      onSave={(name) =>
        mutation.mutate(
          { quote_id: quoteId, phase_id: phaseId, quotePhaseUpdate: { name } },
          {
            onSuccess: close,
            onError: (error) => handleError(error, showToast),
          },
        )
      }
      onCancel={close}
    />
  )
}
